/**
 * ModalInMobile gives possibility separate part of existing DOM so that in mobile views that
 * fragment is shown in a separate modal layer on top of the page.
 *
 * Currently, this does not implement resize listener for window.
 *
 * Example:
 * <Parent>
 *   <ModalInMobile isModalOpenOnMobile={this.state.modalOpen} onClose={handleClose}>
 *     <FormX />
 *   </ModalInMobile>
 * </Parent>
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Modal } from '../../components';
import { withViewport } from '../../util/uiHelpers';

import css from './ModalInMobile.module.css';

function ModalInMobileComponent(props) {
  const {
    children = null,
    className = null,
    containerClassName = null,
    scrollLayerClassName = null,
    id,
    showAsModalMaxWidth = 0,
    closeButtonMessage = null,
    onManageDisableScrolling,
    viewport,
    usePortal = false,
    isModalOpenOnMobile,
    onClose = () => {},
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // After Mounting, component can adapt to responsive screen size
    const isMobileLayout = viewport.width <= showAsModalMaxWidth;

    if (isMobileLayout && isModalOpenOnMobile) {
      setIsOpen(isModalOpenOnMobile);
    }
  }, [isModalOpenOnMobile, showAsModalMaxWidth, viewport.width]);

  useEffect(() => {
    const isChanging = isModalOpenOnMobile !== isOpen;
    const isMobileLayout = viewport.width <= showAsModalMaxWidth;
    const shouldBeClosedAsModal = !isMobileLayout && !isModalOpenOnMobile;

    // Handle change if status is changing on mobile layout or it is closing (on desktop layout)
    if (isChanging && (isMobileLayout || shouldBeClosedAsModal)) {
      setIsOpen(isModalOpenOnMobile);
    }
  }, [isModalOpenOnMobile, showAsModalMaxWidth, viewport.width, isOpen]);

  const handleClose = event => {
    setIsOpen(false);
    if (onClose) onClose(event);
  };

  const isMobileLayout = viewport.width <= showAsModalMaxWidth;
  const isClosedInMobile = isMobileLayout && !isOpen;
  const isOpenInMobile = isOpen && isMobileLayout;

  // We have 3 view states:
  // - default desktop layout (just an extra wrapper)
  // - mobile layout: content visible inside modal popup
  // - mobile layout: content hidden
  const closedClassName = isClosedInMobile ? css.modalHidden : null;
  const classes = classNames(css.root, className);

  return (
    <Modal
      className={classes}
      containerClassName={containerClassName || css.modalContainer}
      contentClassName={css.modalContent}
      scrollLayerClassName={scrollLayerClassName}
      id={id}
      isOpen={isOpenInMobile}
      isClosedClassName={closedClassName}
      onClose={handleClose}
      closeButtonMessage={closeButtonMessage}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal={usePortal && isOpenInMobile}
    >
      {children}
    </Modal>
  );
}

const { bool, func, node, number, string, shape } = PropTypes;

ModalInMobileComponent.propTypes = {
  children: node,
  className: string,
  containerClassName: string,
  id: string.isRequired,
  isModalOpenOnMobile: bool.isRequired,
  onClose: func,
  showAsModalMaxWidth: number,
  closeButtonMessage: node,

  onManageDisableScrolling: func.isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
};

const ModalInMobile = withViewport(ModalInMobileComponent);

export default ModalInMobile;
